import React from 'react';
import { User, Users } from 'lucide-react';
import { DayScale } from './scale';
import { ETAnnotation } from './types';
import { timelineColors, timelineFontWeight } from './tokens';

export const ANNOTATION_LANE_HEIGHT = 28;

const DefaultAnnotationPill = ({ count }: { count: number }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      background: timelineColors.ink,
      color: timelineColors.surface,
      fontSize: 10,
      fontWeight: timelineFontWeight.label,
      padding: '3px 8px',
      borderRadius: 999
    }}
  >
    {count === 1 ? <User size={12} /> : <Users size={12} />}
    {count}
  </div>
);

// "Cohort ribbon" — the new visual treatment for the annotation lane (no
// equivalent in the design doc). A dedicated band, not a lane itself: flat
// background + hairline top/bottom borders so it reads as an instrument
// scale rather than timeline content, directly under the header and above
// the first lane. Each bucket is a dark pill matching the +N badge / hover
// tooltip surface ("dark chrome = meta-information about the timeline").
export const AnnotationLane = React.memo(
  ({
    annotations,
    scale,
    top,
    canvasHeight,
    renderAnnotation,
    borderColor
  }: {
    annotations: ETAnnotation[];
    scale: DayScale;
    top: number;
    canvasHeight: number;
    renderAnnotation?: (a: ETAnnotation) => React.ReactNode;
    borderColor: string;
  }) => (
    <>
      <div
        style={{
          position: 'absolute',
          left: scale.labelWidth,
          top,
          width: scale.totalWidth - scale.labelWidth,
          height: ANNOTATION_LANE_HEIGHT,
          borderTop: `1px solid ${borderColor}`,
          borderBottom: `1px solid ${borderColor}`,
          zIndex: 1
        }}
      />
      {annotations.map((a) => {
        const x = scale.xOfDayCenter(a.day);
        return (
          <React.Fragment key={a.key}>
            <div
              style={{
                position: 'absolute',
                left: x,
                top,
                height: ANNOTATION_LANE_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                transform: 'translateX(-50%)',
                zIndex: 3,
                cursor: 'pointer'
              }}
            >
              {renderAnnotation ? renderAnnotation(a) : <DefaultAnnotationPill count={a.count} />}
            </div>
            <div
              style={{
                position: 'absolute',
                left: x,
                top: top + ANNOTATION_LANE_HEIGHT,
                width: 1,
                height: canvasHeight - (top + ANNOTATION_LANE_HEIGHT),
                background: `${borderColor}40`,
                borderLeft: `1px dashed ${borderColor}`,
                zIndex: 1
              }}
            />
          </React.Fragment>
        );
      })}
    </>
  )
);
